import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import AntLocalization from '../utils/AntLocalization';
import AntCondition from '../utils/AntCondition';
import { Validator } from 'devextreme-react/validator';
import { TagBox } from 'devextreme-react';
import AntQueryStore from './AntQueryStore';
import Tabs from 'devextreme-react/tabs';

export default forwardRef(function (props, ref) {
    const valueExpr = props.options?.valueExpr ?? 'code';
    const displayExpr = props.options?.displayExpr ?? 'name';
    const popUpWidth = props.width ?? '100%';
    const popUpHeight = props.height ?? 250;

    const [mainValue, setMainValue] = useState(props.defaultValue);
    const tagBox = useRef();

    useImperativeHandle(ref, () => tagBox.current?.instance);

    const initClientDataSource = useCallback((_mainValue) => {
        let items = Array.isArray(props.options.dataSource) ? props.options.dataSource : [];

        if (!props.isRemote && items.length > 0) {
            items = items.map((v) => {
                if (typeof v === 'object') {
                    return { ...v, [displayExpr]: v[displayExpr] ?? v[valueExpr] };
                }
                return { [valueExpr]: v, [displayExpr]: v };
            });
        }

        if (Array.isArray(_mainValue)) {
            const existingValues = new Set(items.map(d => String(d[valueExpr])));
            _mainValue.forEach((v) => {
                if (!existingValues.has(String(v))) {
                    items.push({ [valueExpr]: v, [displayExpr]: v });
                }
            });
        }

        return items.map((o) => ({
            [valueExpr]: o[valueExpr],
            [displayExpr]: AntLocalization.translateText(AntCondition.getValue({}, o[displayExpr]))
        }));
    }, [props.options.dataSource, props.isRemote, displayExpr, valueExpr]);

    const getSearchConditions = useCallback(() => {
        const obj = {
            lkTableName: props.lkTableName ?? props.options?.table ?? props.table,
            lkName: !props.url && props.lookupName ? props.lookupName : undefined
        };

        if (props.searchConditions && typeof props.searchConditions === 'object') {
            Object.entries(props.searchConditions).forEach(([k, v]) => {
                if (v !== undefined) obj[k] = v;
            });
        }

        const formData = props.formRef?.current?.instance?.option('formData');
        if (props.parentFields && formData) {
            props.parentFields.forEach((field) => {
                if (typeof field === 'object' && field !== null) {
                    const { field: parentField, alias: aliasField } = field;
                    const val = formData[parentField];
                    if (val !== undefined && val !== null) obj[aliasField] = val;
                } else {
                    const val = formData[field];
                    if (val !== undefined && val !== null) obj[field] = val;
                }
            });
        }

        Object.keys(obj).forEach((key) => {
            if (obj[key] === null || obj[key] === undefined) delete obj[key];
        });

        return obj;
    }, [props.lkTableName, props.lookupName, props.options?.table, props.table, props.url, props.searchConditions, props.parentFields, props.formRef]);

    const initRemoteDataSource = useCallback((_mainValue) => {
        if (!props.isRemote) return null;

        const store = new AntQueryStore({
            key: props.options.keyExpr ?? 'id',
            API: props.API,
            url: props.url,
            loadFunc: 'postLookup',
            isScrolling: true,
            remotePaging: props.remotePaging ?? true,
            getSearchConditions
        });

        const formData = props.formRef?.current?.instance?.option('formData');
        const formField = props.formDisplayField ?? String(props.dataField).replace('_code', '_name');

        if (formData?.[formField] !== undefined) {
            store.addDefaultData([{
                [valueExpr]: formData[props.dataField],
                [displayExpr]: formData[formField]
            }]);
        }

        if (Array.isArray(_mainValue)) {
            store.addDefaultData(_mainValue.map(v => ({ [valueExpr]: v, [displayExpr]: v })));
        }

        return store;
    }, [getSearchConditions, props.isRemote, props.options.keyExpr, props.API, props.url, props.remotePaging, props.formRef, props.formDisplayField, props.dataField, valueExpr, displayExpr]);

    const renderItem = useCallback((item) => {
        const isDisabled = item.disabled || !props.focusStateEnabled;
        const className = `dx-tag-content ${isDisabled ? 'dx-disabled-tag-content' : ''}`.trim();

        return (
            <div className={className}>
                <span>{item.name}</span>
                {!isDisabled && <div className="dx-tag-remove-button" />}
            </div>
        );
    }, [props.focusStateEnabled]);

    const clientDatasource = useMemo(() => initClientDataSource(mainValue), [initClientDataSource, mainValue]);
    const remoteDataSource = useMemo(() => initRemoteDataSource(mainValue), [initRemoteDataSource, mainValue]);

    const onOpened = useCallback((e) => {
        if (props.acceptCustomValue) {
            setTimeout(() => e.component.focus());
        }

        if (props.refreshOnOpened || props.options?.refreshOnOpened) {
            if (props.isRemote) remoteDataSource?.refresh();
        }
    }, [props.acceptCustomValue, props.isRemote, props.refreshOnOpened, props.options?.refreshOnOpened, remoteDataSource]);

    const onCustomItemCreating = useCallback((args) => {
        if (!args.text) {
            args.customItem = null;
            return;
        }

        const obj = { [valueExpr]: args.text, [displayExpr]: args.text };

        if (props.isRemote) {
            args.customItem = remoteDataSource?.addDefaultData([obj]);
        } else {
            args.customItem = obj;
            if (Array.isArray(clientDatasource)) clientDatasource.push(obj);
        }
    }, [valueExpr, displayExpr, props.isRemote, remoteDataSource, clientDatasource]);

    const tabItemRender = useCallback((data) => {
        let strText = `${data[displayExpr]}`;
        if (props.onCustomText) strText = props.onCustomText(data, strText);

        const isSelected = mainValue && Array.isArray(mainValue) && mainValue.includes(data[valueExpr]);
        const className = `ant-tags-select-bar-item ${isSelected ? 'ant-tags-select-bar-item-selected' : ''}`.trim();

        return <div className={className}>{strText}</div>;
    }, [displayExpr, props.onCustomText, mainValue, valueExpr]);

    const onTabItemClick = useCallback((e) => {
        const item = e.itemData;
        const fValue = item[valueExpr];
        const oldValue = mainValue;

        let newValue = Array.isArray(mainValue) ? [...mainValue] : [];
        if (newValue.includes(fValue)) {
            newValue = newValue.filter(v => v !== fValue);
        } else {
            newValue.push(fValue);
        }

        props.onValueChanged?.({ value: newValue, rowData: item, previousValue: oldValue });
    }, [props.onValueChanged, mainValue, valueExpr]);

    const tabValues = useMemo(() => {
        let ar = props.isRemote ? (remoteDataSource?.instance() ?? []) : (clientDatasource ?? []);
        if (!Array.isArray(ar)) ar = [];
        return ar.filter(a => !Array.isArray(mainValue) || !mainValue.includes(a[valueExpr]));
    }, [props.isRemote, remoteDataSource, clientDatasource, mainValue, valueExpr]);

    const canAddNew = useMemo(() => props.options?.allowNew !== true && props.acceptCustomValue, [props.options?.allowNew, props.acceptCustomValue]);
    const isShowTabs = useMemo(() => Boolean(props.options?.showTabs), [props.options?.showTabs]);
    const isHideTagsWhenNoneSelected = useMemo(() => isShowTabs && !canAddNew && props.options?.autoHideTags, [canAddNew, isShowTabs, props.options?.autoHideTags]);

    return (
        <React.Fragment>
            {((Array.isArray(mainValue) && mainValue.length > 0) || !isHideTagsWhenNoneSelected) && (
                <TagBox
                    ref={tagBox}
                    acceptCustomValue={canAddNew}
                    onCustomItemCreating={onCustomItemCreating}
                    searchEnabled={true}
                    searchMode="contains"
                    searchTimeout={200}
                    showDataBeforeSearch={true}
                    placeholder={props.placeholder ?? '...'}
                    showClearButton={props.showClearButton ?? true}
                    showDropDownButton={props.showDropDownButton ?? false}
                    {...props.options}
                    focusStateEnabled={props.focusStateEnabled ?? props.options?.focusStateEnabled ?? true}
                    noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                    defaultValue={mainValue}
                    dataSource={props.isRemote ? remoteDataSource?.instance() : null}
                    items={!props.isRemote ? clientDatasource : null}
                    valueExpr={valueExpr}
                    displayExpr={displayExpr}
                    dropDownOptions={{ width: popUpWidth, height: popUpHeight }}
                    onOpened={onOpened}
                    onFocusIn={(e) => {
                        const inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        props.onFocusIn?.({ dataField: props.dataField });
                    }}
                    onFocusOut={(e) => {
                        const newValue = e.component.option('value');
                        if (newValue !== mainValue) setMainValue(newValue);
                    }}
                    onValueChanged={(e) => {
                        props.onValueChanged?.({ value: e.value, previousValue: e.previousValue });
                    }}
                    tagRender={renderItem}
                >
                    {props.validationRules && <Validator validationRules={props.validationRules} dataField={props.dataField} />}
                </TagBox>
            )}
            {isShowTabs && (
                <Tabs
                    className="ant-tags-select-bar"
                    noDataText=""
                    itemRender={tabItemRender}
                    selectedItem={mainValue}
                    animationEnabled={false}
                    swipeEnabled={false}
                    onItemClick={onTabItemClick}
                    items={tabValues}
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                />
            )}
        </React.Fragment>
    );
});
